/**
 * Role and UI gates for the two-step Mark as Paid → Send to Xero workflow.
 * Server mutations must also enforce finance/admin; these helpers keep UI consistent.
 */
import { SINGAPORE_TIME_ZONE } from "./timezone";
import {
  canSendXeroPayment as canSendXeroPaymentByStatus,
  isXeroPaymentSent,
} from "./xeroPaymentTransmission";

export const MARK_PAID_CONFIRM_TITLE = "Record payment?";
/** First paragraph of the confirmation dialog. */
export const MARK_PAID_CONFIRM_MESSAGE_P1 =
  "Please confirm that the payment has been received before continuing.";
/** Second paragraph without emphasis markers (plain-text / tests). */
export const MARK_PAID_CONFIRM_MESSAGE_P2 =
  "This action records the payment as received but does not send it to Xero.";
/** @deprecated Prefer P1 + P2; kept as concatenated plain text for simple asserts. */
export const MARK_PAID_CONFIRM_MESSAGE = `${MARK_PAID_CONFIRM_MESSAGE_P1}\n\n${MARK_PAID_CONFIRM_MESSAGE_P2}`;
export const MARK_PAID_CONFIRM_ACTION = "Record payment only";
export const MARK_PAID_CONFIRM_ACTION_WITH_RECEIPT = "Record payment and send receipt";
export const MARK_PAID_CONFIRM_CANCEL = "Cancel";
export const MARK_PAID_RECORD_ONLY_HINT =
  "Records payment internally. The customer will not receive an email.";
export const MARK_PAID_RECORD_AND_SEND_HINT =
  "Records payment and emails the customer a payment receipt. Confirm the recipient before continuing.";

export function canUserMarkInvoicePaid(role: string | null | undefined): boolean {
  const r = String(role ?? "").trim().toLowerCase();
  return r === "finance" || r === "admin";
}

export function canUserSendXeroPayment(role: string | null | undefined): boolean {
  return canUserMarkInvoicePaid(role);
}

/** UI: show Mark as Paid only for finance/admin on unpaid invoices. */
export function shouldShowMarkAsPaidButton(input: {
  role: string | null | undefined;
  invoicePaid: boolean;
}): boolean {
  return canUserMarkInvoicePaid(input.role) && !input.invoicePaid;
}

/** UI: show Send to Xero only when paid, not yet sent, and finance/admin. */
export function shouldShowSendToXeroButton(input: {
  role: string | null | undefined;
  invoicePaid: boolean;
  xeroPaymentSyncStatus?: string | null;
}): boolean {
  if (!canUserSendXeroPayment(input.role)) return false;
  return canSendXeroPaymentByStatus({
    invoicePaid: input.invoicePaid,
    xeroPaymentSyncStatus: input.xeroPaymentSyncStatus,
  });
}

export function shouldShowSentToXeroStatus(input: {
  invoicePaid: boolean;
  xeroPaymentSyncStatus?: string | null;
}): boolean {
  return input.invoicePaid && isXeroPaymentSent(input.xeroPaymentSyncStatus);
}

/** Resolve actor from the latest successful payment_synced audit event. */
export function resolveXeroPaymentSentBy(
  auditLog: Array<{ type?: string; actorName?: string | null }> | null | undefined,
): string | null {
  if (!Array.isArray(auditLog)) return null;
  for (let i = auditLog.length - 1; i >= 0; i--) {
    const e = auditLog[i];
    if (e?.type === "xero.payment_synced" && typeof e.actorName === "string" && e.actorName.trim()) {
      return e.actorName.trim();
    }
  }
  return null;
}

export type XeroPaymentSentDisplay = {
  title: string;
  dateLine: string | null;
  timeLine: string | null;
  byLine: string | null;
};

/** Completed "Sent to Xero" status lines (Singapore local date/time). */
export function formatXeroPaymentSentDisplay(input: {
  syncedAt?: Date | string | null;
  sentBy?: string | null;
}): XeroPaymentSentDisplay {
  const title = "✓ Sent to Xero";
  const raw = input.syncedAt;
  if (raw == null) {
    return {
      title,
      dateLine: null,
      timeLine: null,
      byLine: input.sentBy?.trim() ? `by ${input.sentBy.trim()}` : null,
    };
  }
  const d = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return {
      title,
      dateLine: null,
      timeLine: null,
      byLine: input.sentBy?.trim() ? `by ${input.sentBy.trim()}` : null,
    };
  }
  const dateLine = new Intl.DateTimeFormat("en-GB", {
    timeZone: SINGAPORE_TIME_ZONE,
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(d);
  const timeLine = new Intl.DateTimeFormat("en-SG", {
    timeZone: SINGAPORE_TIME_ZONE,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);
  return {
    title,
    dateLine,
    timeLine,
    byLine: input.sentBy?.trim() ? `by ${input.sentBy.trim()}` : null,
  };
}
